import { useState } from 'react'
import {
  Controller,
  useController,
  type Control,
  type FieldValues,
  type Path,
  type RegisterOptions,
} from 'react-hook-form'
import dayjs from 'dayjs'
import { cn } from '@/lib/utils'
import { Textarea } from './textarea'
import { TimeInput } from '../TimeInput'
import { DateInput } from '../DateInput'
import { formatHmm, parseHmm } from '@/lib/flight-log-utils'
import {
  PrntrInput,
  PrntrPhoneRow,
  PrntrPickerRow,
  PrntrSelect,
  PrntrSwitchRow,
  PrntrTextareaShell,
  PrntrValue,
  prntrInputClass,
  prntrTextareaClass,
} from './prntr-input'

// react-hook-form Controller wrappers rendering into the PRNTR Input row
// shell (./prntr-input.tsx). Editors are bare right-aligned inputs; readOnly
// rows render plain text. Plain-state forms should use the prntr-input
// primitives directly instead.

interface RowFieldBaseProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label: React.ReactNode
  readOnly?: boolean
  error?: string
}

// Live input mask for "h:mm" durations. Hours are unbounded (running totals
// like 3443:23), so no colon is auto-inserted — the user types the separator
// (":", or "." / "," which convert, so mobile decimal keypads work). Bare
// digits normalize to whole hours on blur ("13" → "13:00").
export function formatHmmDraft(raw: string): string {
  const text = raw.replace(/[.,;]/g, ':')
  const colonIdx = text.indexOf(':')
  if (colonIdx !== -1) {
    const h = text.slice(0, colonIdx).replace(/\D/g, '').slice(0, 5)
    const m = text.slice(colonIdx + 1).replace(/\D/g, '').slice(0, 2)
    return `${h}:${m}`
  }
  return text.replace(/\D/g, '').slice(0, 5)
}

// Bare "h:mm" input (used inside rows and matrix cells)
export function BareHmmInput({
  value,
  onChange,
  onBlur,
  align = 'right',
  tone = 'default',
  className,
}: {
  value: string | null
  onChange: (next: string) => void
  onBlur?: () => void
  align?: 'right' | 'center'
  tone?: 'default' | 'info'
  className?: string
}) {
  return (
    <input
      type="text"
      inputMode="decimal"
      placeholder="h:mm"
      value={value ?? ''}
      onChange={(e) => onChange(formatHmmDraft(e.target.value))}
      onBlur={() => {
        const minutes = parseHmm(value)
        if (minutes !== null) onChange(formatHmm(minutes))
        onBlur?.()
      }}
      className={cn(
        prntrInputClass,
        align === 'center' && 'text-center',
        tone === 'info' && 'text-content-info-bold',
        className
      )}
    />
  )
}

// Bare numeric input storing number | null. While focused the raw text is
// kept in a draft so partial input like "0." survives the keystroke
// (committing the parsed number on every change would eat the trailing dot).
export function BareNumInput({
  value,
  onChange,
  onBlur,
  integer,
  placeholder,
  align = 'right',
  className,
}: {
  value: number | null
  onChange: (next: number | null) => void
  onBlur?: () => void
  integer?: boolean
  placeholder?: string
  align?: 'right' | 'center'
  className?: string
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const committed = value === null || value === undefined ? '' : String(value)

  return (
    <input
      type="text"
      inputMode={integer ? 'numeric' : 'decimal'}
      placeholder={placeholder}
      value={draft ?? committed}
      onFocus={() => setDraft(committed)}
      onChange={(e) => {
        // Numbers only: integer mode allows digits; decimal mode also allows a
        // leading minus (temps can be negative) and one decimal point.
        let raw = e.target.value
        if (integer) {
          raw = raw.replace(/\D/g, '')
        } else {
          raw = raw.replace(/,/g, '.').replace(/[^0-9.-]/g, '')
          const sign = raw.startsWith('-') ? '-' : ''
          const [head, ...rest] = raw.replace(/-/g, '').split('.')
          raw = sign + head + (rest.length > 0 ? `.${rest.join('')}` : '')
        }
        setDraft(raw)
        const trimmed = raw.trim()
        if (trimmed === '') {
          onChange(null)
          return
        }
        const parsed = integer ? parseInt(trimmed, 10) : parseFloat(trimmed)
        onChange(Number.isNaN(parsed) ? null : parsed)
      }}
      onBlur={() => {
        setDraft(null)
        onBlur?.()
      }}
      className={cn(prntrInputClass, align === 'center' && 'text-center', className)}
    />
  )
}

export function TextRow<T extends FieldValues>({
  control,
  name,
  label,
  labelPill,
  iconLeft,
  type = 'text',
  inputMode,
  autoCapitalize,
  placeholder,
  suffix,
  // Extra right-side node while editing (e.g. an async "Checking…" hint)
  decoration,
  // Empty input commits null (flight-log style nullable models). Set false
  // for z.string() schemas where '' must stay a string.
  nullable = true,
  // Controller-level rules (e.g. async uniqueness validate)
  rules,
  readOnly,
  error,
}: RowFieldBaseProps<T> & {
  labelPill?: React.ReactNode
  iconLeft?: React.ReactNode
  type?: 'text' | 'email' | 'tel' | 'url' | 'password'
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  autoCapitalize?: string
  placeholder?: string
  suffix?: string
  decoration?: React.ReactNode
  nullable?: boolean
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >
}) {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <PrntrInput
          label={label}
          labelPill={labelPill}
          iconLeft={iconLeft}
          suffix={suffix}
          empty={!field.value}
          error={error}
        >
          {readOnly ? (
            <PrntrValue value={(field.value as string | null) ?? null} placeholder="—" />
          ) : (
            <>
              <input
                type={type}
                inputMode={inputMode}
                autoCapitalize={autoCapitalize}
                placeholder={placeholder ?? 'Add'}
                value={(field.value as string | null) ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value === '' && nullable ? null : e.target.value)
                }
                onBlur={field.onBlur}
                className={prntrInputClass}
              />
              {decoration}
            </>
          )}
        </PrntrInput>
      )}
    />
  )
}

export function NumRow<T extends FieldValues>({
  control,
  name,
  label,
  integer,
  placeholder,
  suffix,
  readOnly,
  error,
}: RowFieldBaseProps<T> & { integer?: boolean; placeholder?: string; suffix?: string }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <PrntrInput
          label={label}
          suffix={suffix}
          empty={field.value === null || field.value === undefined}
          error={error}
        >
          {readOnly ? (
            <PrntrValue
              value={field.value === null || field.value === undefined ? null : String(field.value)}
              placeholder="—"
            />
          ) : (
            <BareNumInput
              value={(field.value as number | null) ?? null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              integer={integer}
              placeholder={placeholder ?? '0'}
            />
          )}
        </PrntrInput>
      )}
    />
  )
}

// "h:mm" duration row. `auto` renders the blue Auto pill and tints the value
// while it still holds an auto-computed figure — the input stays editable.
export function HmmRow<T extends FieldValues>({
  control,
  name,
  label,
  suffix,
  auto,
  readOnly,
  error,
}: RowFieldBaseProps<T> & { suffix?: string; auto?: boolean }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <PrntrInput
          label={label}
          suffix={suffix}
          autoPill={auto}
          empty={!field.value}
          error={error}
        >
          {readOnly ? (
            <PrntrValue
              value={(field.value as string | null) ?? null}
              placeholder="—"
              tone={auto ? 'info' : 'default'}
            />
          ) : (
            <BareHmmInput
              value={(field.value as string | null) ?? null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              tone={auto ? 'info' : 'default'}
              className="w-20 shrink-0 grow-0"
            />
          )}
        </PrntrInput>
      )}
    />
  )
}

// "HH:mm" (UTC) clock row on top of the app's TimeInput, which keeps the
// native-vs-web input split. The box styling is stripped so it sits bare in
// the row; iOS's ::-webkit-date-and-time-value is re-aligned right (the app
// globally forces it left).
export function ClockRow<T extends FieldValues>({
  control,
  name,
  label,
  suffix = 'UTC',
  readOnly,
  error,
}: RowFieldBaseProps<T> & { suffix?: string }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <PrntrInput label={label} suffix={suffix} empty={!field.value} error={error}>
          {readOnly ? (
            <PrntrValue value={(field.value as string | null) ?? null} placeholder="—" />
          ) : (
            <TimeInput
              value={field.value ? dayjs(`2000-01-01T${field.value as string}:00`).toDate() : null}
              onChange={(date) => field.onChange(date ? dayjs(date).format('HH:mm') : null)}
              hideClockIcon
              className={cn(
                'h-auto w-20 shrink-0 grow-0 rounded-none border-0 bg-transparent p-0 text-right font-semibold shadow-none',
                'focus-visible:ring-0 focus-visible:border-transparent',
                '[&::-webkit-date-and-time-value]:text-right',
                error && 'text-content-negative-bold'
              )}
            />
          )}
        </PrntrInput>
      )}
    />
  )
}

// "YYYY-MM-DD" date row on top of the app's DateInput (native picker inside
// Capacitor, calendar popover on the web). Box styling stripped, calendar
// icon hidden so only the right-aligned value shows.
export function DateRow<T extends FieldValues>({
  control,
  name,
  label,
  minDate,
  readOnly,
  error,
}: RowFieldBaseProps<T> & { minDate?: Date }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <PrntrInput label={label} error={error}>
          {readOnly ? (
            <PrntrValue
              value={field.value ? dayjs(field.value as string).format('MMM D, YYYY') : null}
              placeholder="—"
            />
          ) : (
            <DateInput
              value={field.value ? dayjs(field.value as string).toDate() : undefined}
              onChange={(date) => field.onChange(date ? dayjs(date).format('YYYY-MM-DD') : null)}
              placeholder="Select"
              clearable={false}
              minDate={minDate}
              className={cn(
                'h-auto w-auto justify-end border-0 bg-transparent p-0 shadow-none',
                'focus-within:ring-0 focus-within:border-transparent',
                '[&>div:first-child]:hidden [&_input]:flex-none [&_input]:w-28 [&_input]:text-right [&_input]:font-semibold',
                '[&_input::-webkit-date-and-time-value]:text-right',
                error && '[&_input]:text-content-negative-bold'
              )}
            />
          )}
        </PrntrInput>
      )}
    />
  )
}

// Same as DateRow but for forms that store Date | null / undefined instead of
// a "YYYY-MM-DD" string.
export function DateObjectRow<T extends FieldValues>({
  control,
  name,
  label,
  iconLeft,
  title,
  minDate,
  startMonth,
  endMonth,
  clearable = false,
  className,
  readOnly,
  error,
  onChanged,
}: RowFieldBaseProps<T> & {
  iconLeft?: React.ReactNode
  title?: string
  minDate?: Date
  startMonth?: Date
  endMonth?: Date
  // Show DateInput's clear-X while a value is set
  clearable?: boolean
  // Extra classes for the row shell (e.g. border-b-0)
  className?: string
  // Side effects after a change (e.g. marking a schedule as user-touched)
  onChanged?: (date: Date | undefined) => void
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <PrntrInput label={label} iconLeft={iconLeft} error={error} className={cn('relative', className)}>
          {readOnly ? (
            <PrntrValue
              value={field.value ? dayjs(field.value as Date).format('MMM D, YYYY') : null}
              placeholder="—"
            />
          ) : (
            <>
              {/* Empty state: native date inputs render nothing when empty, so
                  show the design's "Select" placeholder and stretch the input
                  invisibly over the row (same trick as PrntrSelect). */}
              {!field.value && <PrntrValue value={null} placeholder="Select" />}
              <DateInput
                value={(field.value as Date | undefined) ?? undefined}
                onChange={(date) => {
                  field.onChange(date)
                  onChanged?.(date)
                }}
                placeholder="Select"
                clearable={clearable}
                minDate={minDate}
                title={title}
                startMonth={startMonth}
                endMonth={endMonth}
                className={cn(
                  'h-auto w-auto justify-end border-0 bg-transparent p-0 shadow-none',
                  'focus-within:ring-0 focus-within:border-transparent',
                  '[&>div:first-child]:hidden [&_input]:flex-none [&_input]:w-28 [&_input]:text-right [&_input]:font-semibold',
                  '[&_input::-webkit-date-and-time-value]:text-right',
                  error && '[&_input]:text-content-negative-bold',
                  !field.value && 'absolute inset-0 size-full opacity-0 [&_input]:size-full'
                )}
              />
            </>
          )}
        </PrntrInput>
      )}
    />
  )
}

// Native pop-up select bound to a form path
export function SelectRowField<T extends FieldValues, V extends string>({
  control,
  name,
  label,
  labelTone,
  labelPill,
  iconLeft,
  options,
  display,
  placeholder,
  valueTone,
  readOnly,
  error,
  onChanged,
}: RowFieldBaseProps<T> & {
  label: React.ReactNode
  labelTone?: 'default' | 'info'
  labelPill?: React.ReactNode
  iconLeft?: React.ReactNode
  options: readonly { value: V; label: string }[]
  display?: (value: V | null) => string | undefined
  placeholder?: string
  valueTone?: 'default' | 'info'
  // Side effects after a selection
  onChanged?: (value: V) => void
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <PrntrSelect
          label={label}
          labelTone={labelTone}
          labelPill={labelPill}
          iconLeft={iconLeft}
          value={(field.value as V | null) ?? null}
          onChange={(next) => {
            field.onChange(next)
            onChanged?.(next)
          }}
          options={options}
          display={display?.((field.value as V | null) ?? null)}
          placeholder={placeholder}
          valueTone={valueTone}
          readOnly={readOnly}
          error={error}
        />
      )}
    />
  )
}

// Picker-drawer trigger bound to a form path: the row displays the mapped
// selection and opens the caller's picker drawer; the drawer's onSelect
// should write back via setValue/field.onChange with shouldDirty.
export function PickerRowField<T extends FieldValues>({
  control,
  name,
  label,
  labelPill,
  iconLeft,
  display,
  placeholder,
  onOpen,
  clearable,
  hideChevron,
  readOnly,
  error,
}: RowFieldBaseProps<T> & {
  labelPill?: React.ReactNode
  iconLeft?: React.ReactNode
  // Maps the stored value (id/code) to its display string
  display: (value: unknown) => string | null
  placeholder?: string
  onOpen: () => void
  clearable?: boolean
  hideChevron?: boolean
  error?: string
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <PrntrPickerRow
          label={label}
          labelPill={labelPill}
          iconLeft={iconLeft}
          value={display(field.value)}
          placeholder={placeholder}
          onClick={onOpen}
          onClear={clearable ? () => field.onChange(null) : undefined}
          hideChevron={hideChevron}
          readOnly={readOnly}
          error={error}
        />
      )}
    />
  )
}

// Switch row bound to a form path
export function SwitchRowField<T extends FieldValues>({
  control,
  name,
  label,
  iconLeft,
  readOnly,
  error,
  onChanged,
}: RowFieldBaseProps<T> & { iconLeft?: React.ReactNode; onChanged?: (next: boolean) => void }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <PrntrSwitchRow
          label={label}
          iconLeft={iconLeft}
          checked={!!field.value}
          onChange={(next) => {
            field.onChange(next)
            onChanged?.(next)
          }}
          readOnly={readOnly}
          error={error}
        />
      )}
    />
  )
}

// Phone row bound to two form paths (dial code + national number). The
// caller owns the CountryCodePickerDrawer and writes the code back through
// the form (setValue with shouldDirty).
export function PhoneRowField<T extends FieldValues>({
  control,
  codeName,
  numberName,
  label,
  placeholder,
  onCodeClick,
  readOnly,
  error,
}: {
  control: Control<T>
  codeName: Path<T>
  numberName: Path<T>
  label: React.ReactNode
  placeholder?: string
  onCodeClick: () => void
  readOnly?: boolean
  error?: string
}) {
  const code = useController({ control, name: codeName })
  const number = useController({ control, name: numberName })
  return (
    <PrntrPhoneRow
      label={label}
      code={(code.field.value as string | null) || '+1'}
      onCodeClick={onCodeClick}
      number={(number.field.value as string | null) ?? ''}
      onNumberChange={(next) => number.field.onChange(next)}
      onBlur={number.field.onBlur}
      placeholder={placeholder}
      readOnly={readOnly}
      error={error}
    />
  )
}

// Textarea variant: label row + surface-L2 field box
export function TextareaField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  readOnly,
  error,
}: RowFieldBaseProps<T> & { placeholder?: string }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <PrntrTextareaShell label={label} error={error}>
          <Textarea
            readOnly={readOnly}
            placeholder={placeholder ?? 'Enter details'}
            value={(field.value as string | null) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
            className={cn(
              prntrTextareaClass,
              readOnly && 'pointer-events-none',
              error && !readOnly && 'border-border-negative-bold'
            )}
          />
        </PrntrTextareaShell>
      )}
    />
  )
}
